/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import buildEmailSearchOptions, {
  TYPE_DOMAIN,
  TYPE_DOMAIN_START,
  TYPE_EMAIL_START,
} from 'cio/utils/build-email-search-options';
import { cached, tracked } from '@glimmer/tracking';
import { elementContainsTarget } from 'cio/utils/dom';

interface EmailSearchWithSuggestionsArgs {
  term: string;
  autofocus?: boolean;
  inputClassNames?: string;
  onSearch: (term: string | null) => unknown;
  onClearSearch: () => unknown;
  setInitialSearchTerm?: boolean;
}

export default class EmailSearchWithSuggestions extends Component<EmailSearchWithSuggestionsArgs> {
  @tracked isOpen = false;
  @tracked searchTerm = this.args.setInitialSearchTerm === true ? this.args.term : '';
  @tracked focusedItem: { id: string; label: string } | null = null;

  @tracked rootElement: null | HTMLInputElement = null;
  @tracked inputElement: null | HTMLInputElement = null;

  @action
  onInsert(element: HTMLInputElement) {
    this.rootElement = element;

    document.addEventListener('click', this.onClick);
  }

  @action
  onInsertInput(element: HTMLInputElement) {
    this.inputElement = element;
  }

  get autofocus() {
    return this.args.autofocus ?? false;
  }

  @cached
  get searchOptions() {
    return buildEmailSearchOptions(this.searchTerm);
  }

  @action
  onTermChanged() {
    this.searchTerm = this.args.term;
  }

  private focusItem(delta: number) {
    let { focusedItem, searchOptions } = this;

    let currentIndex = focusedItem == null ? -1 : searchOptions.indexOf(focusedItem);
    let targetIndex = currentIndex + delta;

    if (targetIndex < 0) {
      targetIndex = searchOptions.length - 1;
    }

    this.focusedItem = this.searchOptions[targetIndex % searchOptions.length];
  }

  private buildSearchTerm(term: string, type: string | null) {
    let prefix = term.startsWith('*') ? '' : '*';
    let domainPrefix = term.startsWith('@') || term.startsWith('*@') ? '' : '@';
    let suffix = term.endsWith('*') ? '' : '*';

    if (type === TYPE_DOMAIN_START) {
      return `${domainPrefix}${term}${suffix}`;
    } else if (type === TYPE_DOMAIN) {
      return `${prefix}${domainPrefix}${term}`;
    } else if (type === TYPE_EMAIL_START) {
      return `${term}${suffix}`;
    } else {
      return term;
    }
  }

  private close() {
    this.isOpen = false;
    this.focusedItem = null;
  }

  @action
  onFocus() {
    this.isOpen = !isEmpty(this.searchOptions);
  }

  @action
  onKeyUp(event: KeyboardEvent) {
    let { key } = event;

    // Enter will submit the form and close the menu, so
    // we don't want to handle that here.
    if (key === 'Enter') {
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      return this.focusItem(-1);
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      return this.focusItem(1);
    }

    this.isOpen = !isEmpty(this.searchOptions);

    return;
  }

  @action
  onSubmit(event: Event) {
    event.preventDefault();

    this.args.onSearch(this.buildSearchTerm(this.searchTerm, this.focusedItem?.id ?? null));
    this.close();
  }

  @action
  clearSearch() {
    this.args.onClearSearch();
    this.close();
  }

  @action
  onFocusItem(item: { id: string; label: string }) {
    this.focusedItem = item;
  }

  @action
  toggleSelection(item: { id: string; label: string }) {
    this.args.onSearch(this.buildSearchTerm(this.searchTerm, item.id));
    this.close();
  }

  @action
  findDropdown() {
    return this.inputElement;
  }

  @action
  onInput(value: string) {
    this.searchTerm = value;
  }

  willDestroy() {
    super.willDestroy();

    document.removeEventListener('click', this.onClick);
  }

  @action
  onClick(event: MouseEvent) {
    if (this.rootElement == null || !this.isOpen) {
      return;
    }

    let isComponent = this.rootElement === event.target;
    let inComponent = elementContainsTarget(this.rootElement, event.target);

    if (isComponent || inComponent) {
      return;
    }

    this.isOpen = false;
    this.focusedItem = null;
    return;
  }
}
